import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Description as DescriptionIcon,
  Work as WorkIcon,
  AutoFixHigh as AutoFixHighIcon,
  Assessment as AssessmentIcon,
  ArrowForward as ArrowForwardIcon,
  LinkedIn as LinkedInIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  ViewKanban as ViewKanbanIcon
} from '@mui/icons-material';
import './HomePage.css';

const HEADLINES = {
  morning: {
    headline: "🎯 One Job. One CV. One Shot—Get It Right with AI.",
    subheadline: "Each application deserves a resume built to win. Our AI crafts interview-ready CVs tailored for every job you apply to."
  },
  afternoon: {
    headline: "🚀 Built for Interviews. Powered by AI.",
    subheadline: "Join thousands who've landed interviews with CVs tailored to beat ATS filters and impress recruiters—automatically."
  },
  night: {
    headline: "✨ Turn Applications into Interviews—Every Time.",
    subheadline: "We don't just format your resume. We reimagine it for every opportunity, ensuring you get noticed, not ignored."
  }
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentHeadline, setCurrentHeadline] = useState(HEADLINES.morning);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    const updateHeadline = () => {
      const hour = new Date().getHours();
      if (hour >= 6 && hour < 14) {
        setCurrentHeadline(HEADLINES.morning);
      } else if (hour >= 14 && hour < 22) {
        setCurrentHeadline(HEADLINES.afternoon);
      } else {
        setCurrentHeadline(HEADLINES.night);
      }
    };

    // Initial update
    updateHeadline();

    // Set up interval to check time every minute
    const interval = setInterval(updateHeadline, 60000);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="homepage">
      {/* Hero Section */}
      <header className="hero">
        <nav className={`main-nav ${isScrolled ? 'scrolled' : ''}`}>
          <div className="nav-left">
            <h1 className="logo">Resume Doctor</h1>
          </div>
          <div className="nav-right">
            <button className="nav-button" onClick={() => navigate('/login')}>Login</button>
            <button className="nav-button primary" onClick={() => navigate('/login')}>Sign Up</button>
          </div>
        </nav>
        <div className="hero-content">
          <h1 className="rotating-headline">{currentHeadline.headline}</h1>
          <p className="hero-subtitle rotating-subheadline">
            {currentHeadline.subheadline}
          </p>
          <div className="hero-cta">
            <button className="cta-button primary" onClick={() => navigate('/login')}>
              Boost Your Job Search Now
            </button>
            <button className="cta-button secondary" onClick={() => navigate('/login')}>
              See How It Works
            </button>
          </div>
        </div>
      </header>

      {/* Features Grid */}
      <section className="features-grid">
        <div className="feature-card">
          <div className="feature-icon">
            <AutoFixHighIcon />
          </div>
          <h3>Smart Resume Tailoring</h3>
          <p>Transform your resume for each job application with AI-powered optimization that matches job requirements perfectly</p>
          <button className="feature-cta" onClick={() => navigate('/login')}>
            Tailor Your Resume
          </button>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <ViewKanbanIcon />
          </div>
          <h3>Job Application Tracker</h3>
          <p>Manage your job search process with our intuitive kanban board. Track applications, interviews, and offers all in one place</p>
          <button className="feature-cta" onClick={() => navigate('/login')}>
            Track Applications
          </button>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <AssessmentIcon />
          </div>
          <h3>ATS Score & Analysis</h3>
          <p>Get instant feedback on your resume's ATS compatibility and detailed suggestions to improve your chances of getting noticed</p>
          <button className="feature-cta" onClick={() => navigate('/login')}>
            Check ATS Score
          </button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <h2>Your AI-Powered Job Application Partner</h2>
        <p className="section-subtitle">
          Join our growing community of job seekers who are transforming their applications with AI
        </p>
        <div className="testimonial-grid">
          <div className="testimonial-card">
            <p className="testimonial-text">
              "The AI-powered resume tailoring is incredible. It helped me highlight the right skills and experiences for each job I applied to. I'm getting more responses than ever before."
            </p>
            <div className="testimonial-author">
              <p className="author-name">Sarah Chen</p>
              <p className="author-title">Software Developer</p>
            </div>
          </div>
          <div className="testimonial-card">
            <p className="testimonial-text">
              "The job tracking board keeps me organized and focused. I can see exactly where I am in each application process, and the AI suggestions help me improve my chances at every step."
            </p>
            <div className="testimonial-author">
              <p className="author-name">Michael Rodriguez</p>
              <p className="author-title">Product Manager</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <h2>Smart Job Applications Made Simple</h2>
        <p className="section-subtitle">
          Our AI-powered platform helps you create targeted resumes, track applications, and get noticed by employers—all in one place
        </p>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-icon">
              <AutoFixHighIcon />
            </div>
            <h3>AI-Powered Optimization</h3>
            <p>Our AI analyzes job descriptions and optimizes your resume to highlight the most relevant skills and experiences for each position.</p>
          </div>
          <div className="step-card">
            <div className="step-icon">
              <ViewKanbanIcon />
            </div>
            <h3>Smart Application Tracking</h3>
            <p>Keep track of all your applications, interviews, and follow-ups with our intuitive kanban board system.</p>
          </div>
          <div className="step-card">
            <div className="step-icon">
              <AssessmentIcon />
            </div>
            <h3>Instant ATS Analysis</h3>
            <p>Get real-time feedback on your resume's ATS compatibility and suggestions to improve your chances of getting noticed.</p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta">
        <h2>Land Your Dream Job with AI</h2>
        <p>Create targeted resumes that get noticed, track your applications, and get interview-ready—all powered by AI. Start your journey today.</p>
        <button className="cta-button primary" onClick={() => navigate('/login')}>
          Get Started for Free
        </button>
      </section>

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Resume Doctor</h4>
            <p>Get the job you deserve, faster.</p>
          </div>
          <div className="footer-section">
            <h4>Products</h4>
            <ul>
              <li>Score my resume</li>
              <li>Targeted resume</li>
              <li>LinkedIn review</li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Company</h4>
            <ul>
              <li>About us</li>
              <li>Contact</li>
              <li>Privacy</li>
              <li>Terms</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2024 Resume Doctor. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage; 